// frontend/source/portal/components/EditArtworkCard.js
// Modified version to support both category/tag and artist modes

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ImagePicker from "./ImagePicker";
import adminApi from "../../services/adminapi";
import "./EditArtworkCard.css";

const EditArtworkCard = ({
  artwork,
  onRemove,
  onUpdate,
  onViewDetails,
  categoryId,
  tagId,
  artistId, // New prop for artist mode
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(artwork.title);
  const [mediumId, setMediumId] = useState(artwork.mediumId?._id || "");
  const [date, setDate] = useState(
    artwork.date ? new Date(artwork.date).toISOString().split("T")[0] : ""
  );
  const [width, setWidth] = useState(artwork.width || "");
  const [height, setHeight] = useState(artwork.height || "");
  const [mediums, setMediums] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);

  useEffect(() => {
    loadMediums();
  }, []);

  const loadMediums = async () => {
    try {
      const data = await adminApi.getMediums();
      setMediums(data);
    } catch (err) {
      console.error("Failed to load mediums:", err);
    }
  };

  const handleSave = async () => {
    if (title.trim() === "") {
      alert("Artwork title cannot be empty");
      return;
    }

    const hasChanges =
      title !== artwork.title ||
      mediumId !== (artwork.mediumId?._id || "") ||
      date !== (artwork.date || "") ||
      width !== (artwork.width || "") ||
      height !== (artwork.height || "");

    if (!hasChanges) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      const updateData = { title };
      if (mediumId) updateData.mediumId = mediumId;
      if (date) updateData.date = date;
      if (width) updateData.width = parseFloat(width);
      if (height) updateData.height = parseFloat(height);

      await onUpdate(artwork._id, updateData);
      setIsEditing(false);
    } catch (err) {
      setTitle(artwork.title);
      setMediumId(artwork.mediumId?._id || "");
      setDate(
        artwork.date ? new Date(artwork.date).toISOString().split("T")[0] : ""
      );
      setWidth(artwork.width || "");
      setHeight(artwork.height || "");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setTitle(artwork.title);
    setMediumId(artwork.mediumId?._id || "");
    setDate(
      artwork.date ? new Date(artwork.date).toISOString().split("T")[0] : ""
    );
    setWidth(artwork.width || "");
    setHeight(artwork.height || "");
    setIsEditing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleImageSelect = async (imageUrl) => {
    try {
      setIsSaving(true);
      await onUpdate(artwork._id, { image: imageUrl });
    } catch (err) {
      console.error("Failed to update image:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewDetails = () => {
    if (artistId) {
      // Navigate to artist artwork details page
      navigate(`/portal/student/${artistId}/artwork/${artwork._id}`);
    } else {
      // Use custom handler if provided
      onViewDetails(artwork._id);
    }
  };

  return (
    <div className="edit-artwork-card">
      <div className="edit-artwork-card-image-container">
        <img
          src={artwork.image}
          alt={artwork.title}
          onClick={handleViewDetails}
          className="edit-artwork-card-image"
        />
        <button
          onClick={() => setShowImagePicker(true)}
          className="edit-artwork-change-image-btn"
          title="Change image"
        >
          📷
        </button>
      </div>

      {isEditing ? (
        <div className="edit-artwork-form">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleKeyPress}
            className="edit-artwork-input title-input"
            placeholder="Title"
            autoFocus
            disabled={isSaving}
          />
          <select
            value={mediumId}
            onChange={(e) => setMediumId(e.target.value)}
            className="edit-artwork-input medium-select"
            disabled={isSaving}
          >
            <option value="">Select Medium (optional)</option>
            {mediums.map((medium) => (
              <option key={medium._id} value={medium._id}>
                {medium.name}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            onKeyDown={handleKeyPress}
            className="edit-artwork-input date-input"
            disabled={isSaving}
          />
          <div className="edit-artwork-dimensions">
            <input
              type="number"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              onKeyDown={handleKeyPress}
              className="edit-artwork-input dimension-input"
              placeholder="Width (in)"
              step="0.1"
              min="0"
              disabled={isSaving}
            />
            <span className="dimension-separator">×</span>
            <input
              type="number"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              onKeyDown={handleKeyPress}
              className="edit-artwork-input dimension-input"
              placeholder="Height (in)"
              step="0.1"
              min="0"
              disabled={isSaving}
            />
          </div>
          <div className="edit-artwork-buttons">
            <button
              onClick={handleSave}
              className="edit-artwork-btn save-btn"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={handleCancel}
              className="edit-artwork-btn cancel-btn"
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="edit-artwork-info">
          <h3 className="edit-artwork-title">{artwork.title}</h3>
          {artwork.mediumId?.name && (
            <p className="edit-artwork-medium">{artwork.mediumId.name}</p>
          )}
          {artwork.date && (
            <p className="edit-artwork-date">
              {new Date(artwork.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          )}
          {(artwork.width || artwork.height) && (
            <p className="edit-artwork-dimensions-display">
              {artwork.width && `${artwork.width} in`}
              {artwork.width && artwork.height && " × "}
              {artwork.height && `${artwork.height} in`}
            </p>
          )}
          <div className="edit-artwork-actions">
            <button
              onClick={() => setIsEditing(true)}
              className="edit-artwork-action-btn edit-btn"
              title="Edit artwork"
            >
              Edit
            </button>
            <button
              onClick={() => onRemove(artwork._id)}
              className="edit-artwork-action-btn remove-btn"
              title={artistId ? "Remove from artist" : "Remove from tag"}
            >
              Remove
            </button>
          </div>
        </div>
      )}

      {showImagePicker && (
        <ImagePicker
          currentImage={artwork.image}
          onImageSelect={handleImageSelect}
          onClose={() => setShowImagePicker(false)}
        />
      )}
    </div>
  );
};

export default EditArtworkCard;
